import { api } from '@/lib/api';
import type {
  CarmRecord,
  DeclarationFilters,
  ReleaseFilters,
  BondFilters,
  TariffResource,
  TariffFilters,
  DutyCalculatorInput,
  DutyCalculatorResult,
} from './types';

// =============================================================================
// Declarations
// =============================================================================

export async function getDeclarations(filters: DeclarationFilters = {}, bearerToken?: string): Promise<CarmRecord[]> {
  const params: Record<string, unknown> = { ...filters };
  if (bearerToken) params._bearerToken = bearerToken;
  const result = await api.get<CarmRecord[] | CarmRecord>('/v4/carm/declarations', params);
  return Array.isArray(result) ? result : [result];
}

export async function submitDeclaration(body: Record<string, unknown>, bearerToken?: string): Promise<CarmRecord> {
  const payload = bearerToken ? { ...body, _bearerToken: bearerToken } : body;
  return api.post<CarmRecord>('/v4/carm/declarations', payload);
}

// =============================================================================
// Release Status
// =============================================================================

export async function getReleaseStatuses(filters: ReleaseFilters = {}, bearerToken?: string): Promise<CarmRecord[]> {
  const params: Record<string, unknown> = { ...filters };
  if (bearerToken) params._bearerToken = bearerToken;
  const result = await api.get<CarmRecord[] | CarmRecord>('/v4/carm/releases', params);
  return Array.isArray(result) ? result : [result];
}

// =============================================================================
// Bonds
// =============================================================================

export async function getBonds(filters: BondFilters = {}, bearerToken?: string): Promise<CarmRecord[]> {
  const params: Record<string, unknown> = { ...filters };
  if (bearerToken) params._bearerToken = bearerToken;
  const result = await api.get<CarmRecord[] | CarmRecord>('/v4/carm/bonds', params);
  return Array.isArray(result) ? result : [result];
}

// =============================================================================
// Tariffs
// =============================================================================

export interface TariffResponse {
  results: CarmRecord[];
  totalCount: number | null;
}

export async function getTariffs(resource: TariffResource, filters: TariffFilters = {}): Promise<TariffResponse> {
  const result = await api.get<TariffResponse>(`/v4/carm/tariffs/${resource}`, filters as Record<string, unknown>);
  // Handle both old array format and new {results, totalCount} format
  if (Array.isArray(result)) return { results: result, totalCount: null };
  return result;
}

export async function batchTariffs(body: Record<string, unknown>): Promise<CarmRecord> {
  return api.post<CarmRecord>('/v4/carm/tariffs/batch', body);
}

// =============================================================================
// Duty Calculator
// =============================================================================

export async function calculateDutyTax(input: DutyCalculatorInput): Promise<DutyCalculatorResult> {
  return api.post<DutyCalculatorResult>('/v4/carm/duty-calculator', input);
}
